import { useCallback, useState } from "react";
import { Box, Checkbox, FormControl, FormControlLabel, FormHelperText, Grid, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, Typography } from "@mui/material";
import { useStyles } from "./registration-information.style";
import { useRegistrationInformation } from "./registration-information.hook";
import { PruDatePicker } from "src/app/common/components/PruDatePicker";
import { ExamSessionSelectionDialog } from "./exam-session-selection-dialog/exam-session-selection.dialog";
import { ExamSessionOption } from "./exam-session-selection-dialog/use-exam-session-selection.hook";
import { FileUploadPanel } from "./file-upload-panel/file-upload-panel";
import { useTranslation } from "@pruforce/common-adminweb-sdk";
import { AdminRegistrationFormData, RegistrationInformation } from "../../admin-registration.dialog";
import dayjs from "dayjs";
import { InsertInvitation } from "@mui/icons-material";
import { RegistrationInformationErrors } from "./registration-information.validation";
import { constants } from "src/app/common/constants";
import { FileItem } from "./file-upload-panel/file-upload-panel.hook";
interface RegistrationInformationStepProps {
  formData: AdminRegistrationFormData;
  errors: RegistrationInformationErrors;
  clearError: (field: keyof RegistrationInformationErrors) => void;
  validateField: (field: keyof RegistrationInformationErrors) => boolean;
  onChange: (data: Partial<AdminRegistrationFormData>) => void;
}

export const RegistrationInformationStep = ({ formData, errors, clearError, validateField, onChange }: RegistrationInformationStepProps) => {
  const t = useTranslation();
  const { classes, cx } = useStyles();
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const { selectedExamPaper, selectedVenue, selectedSession, selectedRegistrant, registrationInformation } = formData;
  const isReadOnlyForAgent = selectedRegistrant?.accountType === 'Agent';
  const {
    loadVenueOptions,
    venueOptionsLoading,
    venueOptions,
    examModeOptions,
    regionOptions,
    idTypeOptions,
    genderOptions,
    birthDateRange,
  } = useRegistrationInformation({
    examType: selectedExamPaper?.examType || '',
    examCode: selectedExamPaper?.examCode || '',
    examineeId: selectedRegistrant?.examineeId || '',
  });

  const updateRegistrationInformation = useCallback(
    (data: Partial<RegistrationInformation>) => {
      onChange({
        registrationInformation: {
          ...registrationInformation,
          ...data,
        },
      });
    },
    [onChange, registrationInformation],
  );


  const updateSession = (selectedSession: ExamSessionOption | null) => {
    onChange({ selectedSession });
  };

  const updateVenue = (selectedVenue: string) => {
    onChange({
      selectedVenue,
      selectedSession: null,
    });
    clearError("selectedVenue");
    validateField("selectedSession");
  };


  const handleOpenVenue = () => {
    if (venueOptions.length === 0 && !venueOptionsLoading) {
      loadVenueOptions();
    }
  };

  const handleOpenSessionDialog = () => {
    if (selectedVenue) {
      setSessionDialogOpen(true);
    }
  };

  const handleSessionSelect = (session: ExamSessionOption) => {
    updateSession(session);
    updateRegistrationInformation({
      examScheduleId: session.value,
    });
    clearError("selectedSession");
    setSessionDialogOpen(false);
  };

  const onFilesChange = useCallback((evidenceFiles: FileItem[]) => {
    updateRegistrationInformation({ evidenceFiles });
  }, [updateRegistrationInformation])

  return (
    <div className={classes.container}>
      <section className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {t("recruitment.exam.registration.adminRegistration.registrationInformation.section1Title")}
          </Typography>
        </Box>

        <Grid container spacing={1.25}>
          <Grid item xs={3}>
            <FormControl fullWidth size="small" required error={Boolean(errors.selectedVenue)}>
              <InputLabel className={classes.formLabel} id="venue-select-label" required>
                {t("recruitment.exam.registration.adminRegistration.registrationInformation.venue")}
              </InputLabel>
              <Select
                id="venue-select"
                labelId="venue-select-label"
                label={t("recruitment.exam.registration.adminRegistration.registrationInformation.venue")}
                value={selectedVenue}
                required
                onBlur={() => validateField('selectedVenue')}
                onChange={(event) => updateVenue(event.target.value)}
                onOpen={handleOpenVenue}
              >
                {venueOptionsLoading ? (
                  <MenuItem disabled sx={{
                    justifyContent: 'center',
                  }}>
                    {t("recruitment.exam.registration.loading")}
                  </MenuItem>
                ) : venueOptions.length === 0 ? (
                  <MenuItem disabled sx={{
                    justifyContent: 'center',
                  }}>
                    {t("recruitment.exam.registration.empty")}
                  </MenuItem>
                ) : (venueOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                )))}
              </Select>
              {!!errors.selectedVenue && (
                <FormHelperText error>{errors.selectedVenue}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <Box
              className={cx(
                classes.examDateTimeTrigger,
                !Boolean(selectedVenue) && classes.examDateTimeTriggerDisabled,
              )}
              role="button"
              onClick={handleOpenSessionDialog}
            >
              <FormControl fullWidth size="small" required error={Boolean(errors.selectedSession)}>
                <InputLabel className={classes.formLabel} htmlFor="exam-datetime-input" required>
                  {t("recruitment.exam.registration.adminRegistration.registrationInformation.examDateAndTime")}
                </InputLabel>
                <OutlinedInput
                  id="exam-datetime-input"
                  label={t("recruitment.exam.registration.adminRegistration.registrationInformation.examDateAndTime")}
                  value={selectedSession?.label || ''}
                  disabled
                  endAdornment={
                    <InputAdornment position="end">
                      <InsertInvitation color="action" />
                    </InputAdornment>
                  }
                />
                {!!errors.selectedSession && (
                  <FormHelperText error>{errors.selectedSession}</FormHelperText>
                )}
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={3}>
            <FormControl fullWidth size="small">
              <InputLabel className={classes.formLabel} id="exam-mode-select-label">
                {t("recruitment.exam.registration.adminRegistration.registrationInformation.examMode")}
                <span className={classes.examModeAutoTag}>
                  {t("recruitment.exam.registration.adminRegistration.registrationInformation.examModeAuto")}
                </span>
              </InputLabel>
              <Select
                id="exam-mode-select"
                className={classes.examModeReadonlyInput}
                labelId="exam-mode-select-label"
                label={`${t("recruitment.exam.registration.adminRegistration.registrationInformation.examMode")} ${t("recruitment.exam.registration.adminRegistration.registrationInformation.examModeAuto")}`}
                value={selectedSession?.examMode || ''}
                disabled
                IconComponent={() => null}
              >
                {examModeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Typography variant="body2" className={classes.summaryText}>
          <span className={classes.summaryItem}>
            {t("recruitment.exam.registration.adminRegistration.registrationInformation.selectedExamSession")} <span className={classes.summaryStrong}>{selectedSession?.label || '-'}</span>
          </span>
          <span className={classes.summaryItem}>
            {t("recruitment.exam.registration.adminRegistration.registrationInformation.remainingQuota")} <span className={classes.summaryQuota}>{selectedSession?.remainingQuota ?? 0}</span>
          </span>
        </Typography>
      </section>

      <section className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {t("recruitment.exam.registration.adminRegistration.registrationInformation.section2Title")}
        </Typography>

        <Grid container spacing={1.25}>
          <Grid item xs={3}>
            <FormControl fullWidth size="small" required error={Boolean(errors.firstname)}>
              <InputLabel className={classes.formLabel} id="english-given-name-label" required>
                {t("recruitment.exam.registration.adminRegistration.registrationInformation.enFirstName")}
              </InputLabel>
              <OutlinedInput
                id="english-given-name"
                label={t("recruitment.exam.registration.adminRegistration.registrationInformation.enFirstName")}
                value={registrationInformation.firstname}
                required
                disabled={isReadOnlyForAgent}
                onBlur={() => validateField('firstname')}
                onChange={(event) =>
                  updateRegistrationInformation({
                    firstname: event.target.value,
                  })
                }
              />
              {!!errors.firstname && (
                <FormHelperText error>{errors.firstname}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth size="small" required error={Boolean(errors.lastname)}>
              <InputLabel className={classes.formLabel} id="english-surname-label" required>
                {t("recruitment.exam.registration.adminRegistration.registrationInformation.enSurName")}
              </InputLabel>
              <OutlinedInput
                id="english-surname"
                label={t("recruitment.exam.registration.adminRegistration.registrationInformation.enSurName")}
                value={registrationInformation.lastname}
                required
                disabled={isReadOnlyForAgent}
                onBlur={() => validateField('lastname')}
                onChange={(event) =>
                  updateRegistrationInformation({
                    lastname: event.target.value,
                  })
                }
              />
              {!!errors.lastname && (
                <FormHelperText error>{errors.lastname}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth size="small" error={Boolean(errors.localName)}>
              <InputLabel
                className={classes.formLabel}
                id="chinese-name-label"
                required={!Boolean(registrationInformation.noChineseName)}
              >
                {t("localName")}
              </InputLabel>
              <OutlinedInput
                disabled={registrationInformation.noChineseName || isReadOnlyForAgent}
                required={!Boolean(registrationInformation.noChineseName)}
                id="chinese-name"
                label={t("localName")}
                value={registrationInformation.localName}
                onBlur={() => validateField('localName')}
                onChange={(event) =>
                  updateRegistrationInformation({
                    localName: event.target.value,
                  })
                }
              />
              {!!errors.localName && (
                <FormHelperText error>{errors.localName}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth size="small">
              <div className={classes.checkboxWrapper}>
                <FormControlLabel
                  className={classes.noChineseNameControl}
                  control={
                    <Checkbox
                      size="small"
                      disabled={isReadOnlyForAgent}
                      checked={registrationInformation.noChineseName}
                      onChange={(event) => {
                        updateRegistrationInformation({
                          noChineseName: event.target.checked,
                          localName: ''
                        })
                        if (event.target.checked) {
                          clearError('localName');
                        }
                      }}
                    />
                  }
                  label={t("recruitment.exam.registration.adminRegistration.registrationInformation.noChineseNameIndicator")}
                />
              </div>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={1.25}>
          <Grid item xs={3}>
            <FormControl fullWidth size="small" required error={Boolean(errors.identityDocumentType)}>
              <InputLabel className={classes.formLabel} id="hkid-select-label" required>
                {t("recruitment.exam.registration.adminRegistration.registrationInformation.identificationType")}
              </InputLabel>
              <Select
                id="hkid-select"
                labelId="hkid-select-label"
                label={t("recruitment.exam.registration.adminRegistration.registrationInformation.identificationType")}
                value={registrationInformation.identityDocumentType}
                required
                disabled={isReadOnlyForAgent}
                onBlur={() => validateField('identityDocumentType')}
                onChange={(event) =>
                  updateRegistrationInformation({
                    identityDocumentType:
                      event.target.value,
                  })
                }
              >
                {idTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
              {!!errors.identityDocumentType && (
                <FormHelperText error>{errors.identityDocumentType}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={3}>
            <FormControl fullWidth size="small" required error={Boolean(errors.identityDocumentNumber)}>
              <InputLabel className={classes.formLabel} id="identification-no-label" required>
                {t("recruitment.exam.registration.adminRegistration.registrationInformation.identityDocumentNumber")}
              </InputLabel>
              <OutlinedInput
                id="identification-no"
                label={t("recruitment.exam.registration.adminRegistration.registrationInformation.identityDocumentNumber")}
                value={registrationInformation.identityDocumentNumber}
                required
                disabled={isReadOnlyForAgent}
                onBlur={() => validateField('identityDocumentNumber')}
                onChange={(event) =>
                  updateRegistrationInformation({
                    identityDocumentNumber:
                      event.target.value,
                  })
                }
              />
              {!!errors.identityDocumentNumber && (
                <FormHelperText error>{errors.identityDocumentNumber}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={3}>
            <FormControl fullWidth size="small" required error={Boolean(errors.gender)}>
              <InputLabel className={classes.formLabel} id="gender-select-label" required>{t("gender")}</InputLabel>
              <Select
                id="gender-select"
                labelId="gender-select-label"
                label={t("gender")}
                value={registrationInformation.gender}
                required
                disabled={isReadOnlyForAgent}
                onBlur={() => validateField('gender')}
                onChange={(event) =>
                  updateRegistrationInformation({
                    gender: event.target.value,
                  })
                }
              >
                {genderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
              {!!errors.gender && (
                <FormHelperText error>{errors.gender}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={3}>
            <FormControl fullWidth size="small" required error={Boolean(errors.birthday)}>
              <PruDatePicker
                format={constants.Default_Date_Format}
                value={
                  registrationInformation.birthday
                    ? dayjs(
                      registrationInformation.birthday,
                      constants.Default_Date_Format,
                    ).toDate()
                    : null
                }
                disabled={isReadOnlyForAgent}
                onChange={(value) => {
                  updateRegistrationInformation({
                    birthday: value
                      ? dayjs(value).format(constants.Default_Date_Format)
                      : '',
                  });
                  clearError('birthday');
                }}
                maxDate={birthDateRange.maxDate}
                minDate={birthDateRange.minDate}
                slotProps={{
                  textField: {
                    label: t("recruitment.exam.registration.adminRegistration.registrationInformation.dateOfBirth"),
                    InputLabelProps: {
                      className: classes.formLabel,
                    },
                    size: "small",
                    required: true,
                    className: classes.datePickerNoMarginTop,
                    error: Boolean(errors.birthday),
                  },
                }}
              />
              {!!errors.birthday && (
                <FormHelperText error>{errors.birthday}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </section>

      <section className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {t("recruitment.exam.registration.adminRegistration.registrationInformation.section3Title")}
        </Typography>

        <Grid container spacing={1.25}>
          <Grid item xs={3}>
            <FormControl fullWidth size="small" required error={Boolean(errors.countryCode)}>
              <InputLabel className={classes.formLabel} id="region-code-select-label" required>{t("recruitment.exam.registration.adminRegistration.registrationInformation.mobilePrefix")}</InputLabel>
              <Select
                required
                id="region-code-select"
                labelId="region-code-select-label"
                label={t("recruitment.exam.registration.adminRegistration.registrationInformation.mobilePrefix")}
                value={registrationInformation.countryCode}
                renderValue={(value) => value}
                disabled={isReadOnlyForAgent}
                onBlur={() => validateField('countryCode')}
                onChange={(event) =>
                  updateRegistrationInformation({
                    countryCode: event.target.value,
                  })
                }
              >
                {regionOptions.length === 0 ? (
                  <MenuItem disabled sx={{ justifyContent: 'center' }}>
                    {t("recruitment.exam.registration.empty")}
                  </MenuItem>
                ) : (regionOptions.map((option) => (
                  <MenuItem className={classes.regionOption} key={option.key} value={option.value}>
                    <span>{option.value}</span>
                    <span>{option.label}</span>
                  </MenuItem>
                )))}
              </Select>
              {!!errors.countryCode && (
                <FormHelperText error>{errors.countryCode}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={3}>
            <FormControl fullWidth size="small" required error={Boolean(errors.phoneNumber)}>
              <InputLabel className={classes.formLabel} id="mobile-number-label" required>{t("recruitment.exam.registration.adminRegistration.registrationInformation.mobileNumber")}</InputLabel>
              <OutlinedInput
                id="mobile-number"
                label={t("recruitment.exam.registration.adminRegistration.registrationInformation.mobileNumber")}
                value={registrationInformation.phoneNumber}
                required
                disabled={isReadOnlyForAgent}
                onBlur={() => validateField('phoneNumber')}
                onChange={(event) =>
                  updateRegistrationInformation({
                    phoneNumber: event.target.value,
                  })
                }
              />
              {!!errors.phoneNumber && (
                <FormHelperText error>{errors.phoneNumber}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={3}>
            <FormControl fullWidth size="small" required error={Boolean(errors.email)}>
              <InputLabel className={classes.formLabel} id="email-address-label" required>{t("recruitment.exam.registration.adminRegistration.registrationInformation.emailAddress")}</InputLabel>
              <OutlinedInput
                id="email-address"
                label={t("recruitment.exam.registration.adminRegistration.registrationInformation.emailAddress")}
                value={registrationInformation.email}
                required
                disabled={isReadOnlyForAgent}
                onBlur={() => validateField('email')}
                onChange={(event) =>
                  updateRegistrationInformation({
                    email: event.target.value,
                  })
                }
              />
              {!!errors.email && (
                <FormHelperText error>{errors.email}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </section>

      <section className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {t("recruitment.exam.registration.adminRegistration.registrationInformation.section4Title")}
        </Typography>

        <FileUploadPanel
          resetKey={selectedRegistrant?.examineeId ?? ''}
          onFilesChange={onFilesChange}
        />
      </section>

      {selectedVenue && (
        <ExamSessionSelectionDialog
          open={sessionDialogOpen}
          examType={selectedExamPaper?.examType || ''}
          examCode={selectedExamPaper?.examCode || ''}
          examineeId={selectedRegistrant?.examineeId || ''}
          examCentre={selectedVenue}
          selectedSession={selectedSession}
          onClose={() => setSessionDialogOpen(false)}
          onSelect={handleSessionSelect}
        />
      )}
    </div >
  );
};
